import React, { useEffect, useRef, useState } from 'react';
import sharedSupabase from '../config/supabase';

/**
 * "Countertop is still matching related products" — the banner that closes
 * the gap between an import LOOKING finished and its related-product
 * classification actually finishing.
 *
 * A document import saves its items, shows its review, and then classifies
 * relationship pairs in a server-side drain that routinely runs 20–30 minutes
 * longer. Nothing used to say so: a user who opened Related Products five
 * minutes after importing saw a partial set and read it as "matching didn't
 * work" — the exact misread that prompted this banner.
 *
 * Mechanics: polls menu-starter `get_latest_workflow` while the latest
 * workflow's `metadata.import_enrichment` job is pending/running, showing
 * phase-appropriate copy and "N of M pairs reviewed" (the redacted job now
 * carries `relationship_total`). Every poll ALSO matters beyond display:
 * get_latest_workflow runs the server's stale-drain recovery, so a banner on
 * screen is a babysitter for a drain whose self-continuation died.
 *
 * Renders nothing when there is no active job — most mounts, most of the
 * time. On completion it fires `onSettled` once (so the host can refresh
 * relationships/suggestions) and shows a brief "done" state before hiding.
 */

const POLL_MS = 20000;
const DONE_LINGER_MS = 12000;

const PHASE_COPY = {
  pending: 'Preparing to match your imported items…',
  naming_items: 'Standardizing item names…',
  matching_items: 'Matching imported items against your catalog…',
  matching_item_identities: 'Matching imported items against your catalog…',
  pricing_pending: 'Reading objective package sizes…',
  objective_pricing: 'Reading objective package sizes…',
  relationships_pending: 'Preparing related-product matching…',
  screening_related_products: 'Screening imported items for possible matches…',
  matching_related_products: 'Matching related products…',
  preparing_import_review: 'Finishing up…',
};

const styles = {
  banner: {
    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    padding: '8px 12px', marginBottom: 10, borderRadius: 10,
    background: '#F0F8FF', border: '1px solid #cfe4ee', color: '#0c5460', fontSize: 13,
  },
  done: {
    background: '#e9f7ee', border: '1px solid #bfd8c4', color: '#1B7E3C',
  },
  progress: { color: '#5A7A8A', fontSize: 12 },
};

/**
 * The display model for one polled workflow, exported for tests. Returns
 * null when nothing should render: no workflow, no deferred-enrichment job,
 * or a job that settled before we ever saw it running.
 */
export function enrichmentBannerModel(workflow) {
  const job = workflow?.metadata?.import_enrichment;
  if (!job || typeof job !== 'object') return null;
  const status = String(job.status || '');
  if (status !== 'pending' && status !== 'running') return null;
  const phase = String(job.phase || '');
  const total = Number(job.relationship_total);
  const progress = Number(job.relationship_progress ?? job.relationship_cursor);
  const inRelationshipPhase = phase === 'matching_related_products';
  return {
    text: PHASE_COPY[phase] || PHASE_COPY[status] || 'Finishing your import in the background…',
    // Pair counts only mean something once the queue exists and is being
    // drained — a cursor against an unknown total reads as broken.
    progressText: inRelationshipPhase && Number.isFinite(total) && total > 0 && Number.isFinite(progress)
      ? `${Math.min(progress, total)} of ${total} pairs reviewed`
      : null,
    workflowId: workflow?.id ?? null,
  };
}

export default function ImportEnrichmentStatusBanner({ storeId, onSettled }) {
  const [model, setModel] = useState(null);
  const [settled, setSettled] = useState(false);
  const wasActiveRef = useRef(false);
  const settledFiredRef = useRef(false);

  useEffect(() => {
    if (!storeId) { setModel(null); return undefined; }
    let cancelled = false;
    let timer = null;
    wasActiveRef.current = false;
    settledFiredRef.current = false;
    setSettled(false);

    const poll = async () => {
      let workflow = null;
      let readOk = true;
      try {
        const { data, error } = await sharedSupabase.functions.invoke('menu-starter', {
          body: { action: 'get_latest_workflow', store_id: storeId },
        });
        if (error) throw new Error(error.message || 'get_latest_workflow failed');
        workflow = data?.workflow ?? null;
      } catch {
        readOk = false;
      }
      if (cancelled) return;
      if (!readOk) {
        // The banner is informational — an unreadable poll changes nothing on
        // screen. Keep whatever is showing and retry only while a job was
        // already visible; a transient failure must never end in a false
        // "finished" state.
        if (wasActiveRef.current) timer = setTimeout(poll, POLL_MS);
        else setModel(null);
        return;
      }
      const next = enrichmentBannerModel(workflow);
      if (next) {
        wasActiveRef.current = true;
        setModel(next);
        timer = setTimeout(poll, POLL_MS);
        return;
      }
      // No active job. If one WAS active on this screen, it just finished —
      // tell the host once so relationships/suggestions refresh, and linger
      // briefly so the transition is legible instead of a silent vanish.
      if (wasActiveRef.current && !settledFiredRef.current) {
        settledFiredRef.current = true;
        setSettled(true);
        if (typeof onSettled === 'function') onSettled();
        timer = setTimeout(() => { if (!cancelled) { setModel(null); setSettled(false); } }, DONE_LINGER_MS);
        return;
      }
      setModel(null);
    };

    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  if (settled) {
    return (
      <div style={{ ...styles.banner, ...styles.done }} role="status" data-testid="enrichment-banner-done">
        Related-product matching finished — new matches are shown below.
      </div>
    );
  }
  if (!model) return null;
  return (
    <div style={styles.banner} role="status" data-testid="enrichment-banner">
      <span className="spinner-border spinner-border-sm" style={{ flex: '0 0 auto' }} aria-hidden="true" />
      <span>
        {model.text}
        {' '}This continues in the background — matches appear here as they are found.
      </span>
      {model.progressText && <span style={styles.progress}>{model.progressText}</span>}
    </div>
  );
}
